/**
 * The idle world screen's pure parts (D240 to D246): what each producer row shows, which
 * locked producers are revealed, the barn chip, the welcome back's lines, the tips and the
 * words for a refusal. No DOM and no clock: time is passed in.
 */
import type {
  IdleChainT,
  IdleContent,
  IdleDecorationKind,
  IdleGoalT,
  IdleMilestoneT,
  IdlePerkT,
  IdleProducerT,
  IdleUpgradeT,
} from '@hedgerow/data';
import { linkChips, moveRefusal, type LinkChip } from './arrange-view.ts';
import {
  idleBarnCapacityMs,
  idleBarnOverflow,
  idleBarnUpgradeCost,
  idleBarnWaste,
  idleBuyCost,
  idleBuyMax,
  idleChainLock,
  idleDecorationFestivalsNeeded,
  idleDecorationInSeason,
  idleDecorationOwned,
  idleDecorations,
  idleTrimmings,
  idleEffects,
  idleFestivalAdvice,
  idleFestivalAvailable,
  idleFestivalCoinsFor,
  idleFestivalPreview,
  idleProducerFor,
  idleFestivalsNeeded,
  idleFestivalsThisGeneration,
  idleGeneration,
  idleGoalIsLong,
  idleGoalParse,
  idleGoalReward,
  idleGoalTarget,
  idleGoals,
  idleHandTapLeftMs,
  idleIncome,
  idleShownProducers,
  idleOpenLands,
  idleHandCost,
  idleNextMilestone,
  idleProducerStats,
  idleUnitCost,
  idleUnlockCost,
  idlePerkFestivalsNeeded,
  idlePerkNeedsPerk,
  idlePerkTotal,
  idleTraitTotal,
  idleUpgradeCost,
  idleUpgradeTotal,
  IDLE_DECORATION_KINDS,
  type IdleCollectionKind,
  type IdleCollectionView,
  type IdleWorldState,
  type IdleGoalView,
  type IdleRefusal,
  type IdleBarnWaste,
  type IdleSeason,
  type IdleTotal,
  type IdleWelcomeBack,
  type IdleRegionView,
} from '@hedgerow/sim';
import { fmtBigFixed } from '../juice/format.ts';
import { coins, num } from './notation.ts';
import { hasSpriteIcon } from '../ui/Icon.tsx';
import { cap, currencyShort, lower, t as term } from './terms.ts';

// ---------------------------------------------------------------------------------------
// Words.

/** A span of time: `0.6s`, `40s`, `6m`, `1h 20m`, `2h`, `1d 4h`. Cut down, never rounded up. */
export function fmtSpan(ms: number): string {
  // A bar faster than a second reads in tenths, so it never claims to take no time.
  if (ms > 0 && ms < 1000) return `${Math.max(1, Math.floor(ms / 100)) / 10}s`;
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  // Under five minutes the seconds still matter: a market day lasts 1m 30s.
  if (m < 5 && s % 60) return `${m}m ${s % 60}s`;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
}

/**
 * What a milestone gives, in words that never read like a count (D261): `2× faster`,
 * `50% faster`, `pays 2×`, `pays 50% more`.
 */
export function milestoneGain(m: IdleMilestoneT): string {
  const f = m.multiplier;
  const pct = Math.round((f - 1) * 100);
  if (m.kind === 'speed') return f < 2 ? `${pct}% faster` : `${f}× faster`;
  return f < 2 ? `pays ${pct}% more` : `pays ${f}×`;
}

/** The next milestone as the row reads it: `At 10: twice as fast`. */
export function milestoneLabel(m: IdleMilestoneT): string {
  return `At ${m.owned}: ${milestoneGain(m)}`;
}

/** A milestone just reached, as its toast says it: `Wheat at 10: twice as fast`. */
export function milestoneToast(name: string, m: IdleMilestoneT): string {
  return `${name} at ${m.owned}: ${milestoneGain(m)}`;
}

/**
 * Coins a second, the one rate form every row uses (D261): `1.6c/s`, `383c/s`, `15.3Kc/s`.
 * Under ten a second keeps one decimal, cut down, so a slow crop never reads as 0 or 1.
 */
export function rateText(rate: number): string {
  if (rate > 0 && rate < 10) return `${Math.floor(rate * 10) / 10}${currencyShort()}/s`;
  return `${coins(rate)}/s`;
}

/** The clock time of `at`, `18:40`, local; `01:34 tomorrow` when it is tomorrow. */
export function clockAt(at: number, t: number): string {
  const d = new Date(at);
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const today = new Date(t);
  if (d.toDateString() === today.toDateString()) return hm;
  const next = new Date(t);
  next.setDate(next.getDate() + 1);
  if (d.toDateString() === next.toDateString()) return `${hm} tomorrow`;
  return `in ${fmtSpan(at - t)}`;
}

/**
 * The icon for a producer (D259): its own drawing, `packages/art/producers/{id}.svg`, built
 * into the sprite as `producer_{id}`. A producer with no drawing yet shows the sprout; see
 * the note at the top of `idle/map.ts` for adding one.
 */
/** A producer's icon: its theme's own (`producers_{id}`, D348) or the world's (`producer_{id}`). */
export const iconFor = (id: string): string =>
  hasSpriteIcon(`producers_${id}`) ? `producers_${id}` : `producer_${id}`;

/** The words for a refusal, the same check that refused it (D52). */
export function refusalText(
  content: IdleContent,
  r: { reason: IdleRefusal; needs?: number; with?: string },
  producer?: string,
): string {
  switch (r.reason) {
    case 'not_enough_coins':
      return `Needs ${coins(r.needs ?? 0)}`;
    case 'not_ready':
      return 'Still growing';
    case 'has_hand':
      return `${cap(aTerm(term('hand')))} runs it`;
    case 'locked':
      return 'Locked';
    case 'unlock_in_order': {
      const i = content.producers.findIndex((p) => p.id === producer);
      const before = content.producers[i - 1];
      return before ? `Unlock ${before.name.toLowerCase()} first` : 'Unlock the one above first';
    }
    case 'barn_top_level':
      return 'At its top level';
    case 'already_built':
      return 'Already built';
    case 'already_bought':
      return 'Already bought';
    case 'festival_not_ready':
      return `Needs ${coins(r.needs ?? 0)} more this year`;
    case 'perk_top_level':
      return 'At its top level';
    case 'no_offer':
      return 'Gone';
    case 'not_enough_heritage':
      return `Needs ${num(r.needs ?? 0)} ${term('heritage')}`;
    case 'not_enough_lineage':
      return `Needs ${num(r.needs ?? 0)} ${term('lineage')}`;
    case 'trait_top_level':
      return 'At its top level';
    case 'needs_festivals':
      return `Opens after more ${term('festivals')}`;
    case 'needs_perk':
      return 'Needs another perk first';
    case 'pass_on_not_ready':
      return 'Not ready to pass on yet';
    case 'unknown_decoration':
      return 'Not possible now';
    case 'not_enough_trimmings':
      return `Needs ${num(r.needs ?? 0)} ${term('trimmings')}`;
    case 'out_of_season':
      return 'Not on sale this week';
    case 'not_owned':
      return 'Not bought yet';
    case 'tier_locked':
      // An Almanac step names its tier; a perk's tier is short of Heritage spent in its branch.
      return r.needs === undefined ? 'Tier locked' : `Spend ${num(r.needs)} more first`;
    case 'fork_closed':
      return `Closed this ${term('generation')}`;
    case 'too_early':
    case 'no_plot':
    case 'bad_spot':
    case 'too_big':
    case 'other_too_big':
      // D454 step 3: moving plots; the two size reasons name the plot dropped on.
      return moveRefusal(content, r.reason, producer, r.with) ?? 'Not possible now';
    default:
      return 'Not possible now';
  }
}

// ---------------------------------------------------------------------------------------
// Rows.

/** D278, D454: one, ten, up to the next milestone, or as many as the coins cover. */
export type BuyMode = 'one' | 'ten' | 'next' | 'max';

export interface BuyOffer {
  /** How many the button buys. At least 1: with Max and nothing affordable, one. */
  n: number;
  cost: number;
  /** Coins short, when it cannot be bought. */
  needs?: number;
}

export interface ProducerRow {
  id: string;
  name: string;
  icon: string;
  owned: number;
  hand: boolean;
  /** A manual bar that has not been started. A tap starts it. */
  idle: boolean;
  running: boolean;
  /** When the current bar began, on the state's clock: `advancedTo - progressMs`. */
  barStart: number;
  cycleMs: number;
  payout: number;
  tapPayout: number;
  /** Coins a second while it runs. */
  rate: number;
  /** D454: the row's offers, one per setting of the list's switch; the row shows one. */
  buys: Record<BuyMode, BuyOffer>;
  /** Null once a hand is hired. */
  hire: { cost: number; needs?: number } | null;
  /** `25 owned: x1.5 pay`, or null when every milestone is reached. */
  milestone: string | null;
  /** D431: the owned count the next milestone is at (the tick on the bar), or null. */
  milestoneAt: number | null;
  /** D271: the buildings standing on this crop, `Mill x3`. */
  badges: string[];
  /** D454 step 3: what neighbours and the hands' yard do for this plot, on the note line. */
  links: LinkChip[];
  /** D277: hand-run, and taps have sped this bar as far as they may. */
  tapSpent: boolean;
  /** D301: the piece of land beside the world it stands on; absent on the world itself. */
  land?: string;
}

/**
 * The buy button's offer for a producer with `owned` owned and `have` coins. Buy to next
 * (D278) buys exactly what reaches the next milestone, and past the last one is Buy max.
 */
export function buyOffer(p: IdleProducerT, owned: number, have: number, mode: BuyMode): BuyOffer {
  const milestone = mode === 'next' ? idleNextMilestone(p, owned) : undefined;
  if (milestone) {
    const n = Math.max(1, milestone.owned - owned);
    const cost = n === 1 ? idleUnitCost(p, owned) : idleBuyCost(p, owned, n);
    return cost > have ? { n, cost, needs: cost - have } : { n, cost };
  }
  const max = mode === 'one' || mode === 'ten' ? 0 : idleBuyMax(p, owned, have);
  const n = mode === 'ten' ? 10 : Math.max(1, max);
  const cost = n === 1 ? idleUnitCost(p, owned) : idleBuyCost(p, owned, n);
  return cost > have ? { n, cost, needs: cost - have } : { n, cost };
}

function hireOffer(cost: number, have: number): { cost: number; needs?: number } {
  return have >= cost ? { cost } : { cost, needs: cost - have };
}

/** One row per unlocked producer, in the content's order, each with its offers (D454). */
export function producerRows(state: IdleWorldState, content: IdleContent): ProducerRow[] {
  const have = state.year.coins;
  const effects = idleEffects(state, content, state.advancedTo);
  const chips = linkChips(state, content);
  const rows: ProducerRow[] = [];
  for (const p of content.producers) {
    const ps = state.year.producers[p.id];
    if (!ps) continue;
    const stats = idleProducerStats(state, content, p.id, effects);
    // D264: a trait (good stock) can lower how fast costs grow, so costs come from here.
    const priced = idleProducerFor(state, content, p.id) ?? p;
    const next = idleNextMilestone(p, ps.owned);
    rows.push({
      id: p.id,
      name: p.name,
      icon: iconFor(p.id),
      owned: ps.owned,
      hand: ps.hand,
      idle: !ps.hand && !ps.running,
      running: ps.running,
      barStart: state.advancedTo - ps.progressMs,
      cycleMs: ps.cycleMs,
      payout: stats.payout,
      tapPayout: stats.tapPayout,
      rate: stats.rate,
      buys: {
        one: buyOffer(priced, ps.owned, have, 'one'),
        ten: buyOffer(priced, ps.owned, have, 'ten'),
        next: buyOffer(priced, ps.owned, have, 'next'),
        max: buyOffer(priced, ps.owned, have, 'max'),
      },
      hire: ps.hand ? null : hireOffer(idleHandCost(state, content, p.id, effects), have),
      milestone: next ? milestoneLabel(next) : null,
      milestoneAt: next ? next.owned : null,
      tapSpent: ps.hand && idleHandTapLeftMs(state, content, p.id) === 0,
      badges: content.chains
        .filter((c) => c.producer === p.id && (state.year.chains ?? []).includes(c.id))
        .map(chainBadge),
      links: chips[p.id] ?? [],
      ...(p.land !== undefined ? { land: p.land } : {}),
    });
  }
  return rows;
}

/** A bar shorter than this is a flicker, not a bar: it is drawn full and steady. */
export const FAST_BAR_MS = 500;

/** The bar runs faster than the eye can follow. */
export function isFastBar(row: Pick<ProducerRow, 'cycleMs' | 'running'>): boolean {
  return row.running && row.cycleMs < FAST_BAR_MS;
}

/** What a row says it earns: its rate, the one form for every row (D261): `1.6c/s`. */
export function payLabel(row: Pick<ProducerRow, 'rate'>): string {
  return rateText(row.rate);
}

/**
 * Where a bar stands at `t`: when the current cycle began and whether a manual bar is
 * full. A hand-run bar rolls on by whole cycles, so it can be drawn between the store's
 * ticks without the state being advanced.
 */
export function barAt(
  row: Pick<ProducerRow, 'barStart' | 'cycleMs' | 'hand' | 'running'>,
  t: number,
): { start: number; ready: boolean; nextAt: number | null } {
  const { cycleMs } = row;
  if (!row.running) return { start: t, ready: false, nextAt: null };
  if (row.hand) {
    const laps = Math.max(0, Math.floor((t - row.barStart) / cycleMs));
    const start = row.barStart + laps * cycleMs;
    return { start, ready: false, nextAt: start + cycleMs };
  }
  const end = row.barStart + cycleMs;
  return { start: row.barStart, ready: t >= end, nextAt: t >= end ? null : end };
}

export interface ChainRow {
  id: string;
  /** The building's name: Mill. */
  name: string;
  /** The producer it lifts. */
  producer: string;
  /** `Mill: wheat pays three times as much`. */
  label: string;
  /** D439: the card's title, `Build a mill`. */
  title: string;
  /** D439: what it does, `Wheat becomes flour, worth three times as much`. */
  effect: string;
  /** D439: the producer's icon, for the card's tile. */
  icon: string;
  cost: number;
  built: boolean;
  /** Coins short, when not built and not affordable. */
  needs?: number;
  /** Set when a festival perk still gates it (D281), in place of `needs`: "Opens with the X perk". */
  locked?: string;
}

/**
 * D271: a chain is a building on its crop, and says what it changes: `Build a mill: wheat
 * becomes flour, worth three times as much`.
 */
export function chainLabel(content: IdleContent, ch: IdleChainT): string {
  const crop = (
    content.producers.find((p) => p.id === ch.producer)?.name ?? ch.producer
  ).toLowerCase();
  const building = ch.name.toLowerCase();
  const a = /^[aeiou]/.test(building) ? 'an' : 'a';
  // Carrots become, wheat becomes: a name that ends in s is a plural.
  const become = crop.endsWith('s') ? 'become' : 'becomes';
  const f = ch.multiplier;
  const worth = f === 2 ? 'twice' : f === 3 ? 'three times' : `${f} times`;
  return `Build ${a} ${building}: ${crop} ${become} ${ch.product.toLowerCase()}, worth ${worth} as much`;
}

/** D439: a chain's label as the card's two lines: `Build a mill` and what it does. */
export function chainLines(label: string): { title: string; effect: string } {
  const at = label.indexOf(': ');
  if (at < 0) return { title: label, effect: '' };
  const effect = label.slice(at + 2);
  return { title: label.slice(0, at), effect: effect.charAt(0).toUpperCase() + effect.slice(1) };
}

/** D271: a built chain as a badge on its crop's row: `Mill x3`. */
export const chainBadge = (ch: IdleChainT): string => `${ch.name} x${ch.multiplier}`;

/**
 * D261: a row not yet bought shows only when it is the next step or the world has at least
 * this share of its cost, so the screen holds what can be reached soon and nothing more.
 */
export const REVEAL_SHARE = 0.25;

/**
 * The chains whose producer is unlocked and that are not yet built, each under its producer
 * (D244: one at a time), from a quarter of their cost (D261). A built chain leaves the list
 * and stands on the map, with a badge on its crop's row (D271). D365: a chain already in
 * `seen` stays listed for the rest of the year even once the balance drops back under that
 * quarter, so it does not wink out as it is approached.
 */
export function chainRows(
  state: IdleWorldState,
  content: IdleContent,
  seen: ReadonlySet<string> = new Set(),
): ChainRow[] {
  const have = state.year.coins;
  const built = new Set(state.year.chains ?? []);
  const out: ChainRow[] = [];
  for (const ch of content.chains) {
    if (!state.year.producers[ch.producer]) continue;
    if (built.has(ch.id)) continue;
    if (!seen.has(ch.id) && have < ch.cost * REVEAL_SHARE) continue;
    const row: ChainRow = {
      id: ch.id,
      name: ch.name,
      producer: ch.producer,
      label: chainLabel(content, ch),
      ...chainLines(chainLabel(content, ch)),
      icon: iconFor(ch.producer),
      cost: ch.cost,
      built: built.has(ch.id),
    };
    // D281, D343: the same lock buildIdleChain checks, asked here so a gated building never
    // looks buildable (D281): a festival perk, a festival count, or an earlier building on
    // the same crop (the bakery after the mill).
    const lock = idleChainLock(state, content, ch);
    // D377: a building waiting on the one before it stays hidden until that one is built.
    if (lock?.chain !== undefined) continue;
    if (lock?.perk !== undefined) row.locked = `Opens with the ${ch.name} perk`;
    else if (lock?.festivals !== undefined) row.locked = opensAfter(lock.festivals, 0);
    else if (!row.built && have < ch.cost) row.needs = ch.cost - have;
    out.push(row);
  }
  return out;
}

export interface LockedRow {
  id: string;
  name: string;
  icon: string;
  unlockCost: number;
  /** What one pays per bar, so the goal says why it is worth it. */
  payout: number;
  cycleMs: number;
  /** Coins a second it earns once bought. */
  rate: number;
  /** Coins short. */
  needs?: number;
  /** D251: `Opens after 3 festivals`, when it waits on festivals. A quiet goal, no button. */
  opens?: string;
  /** D301: the piece of land beside the world it stands on; absent on the world itself. */
  land?: string;
}

/** `Opens after 3 festivals`, or `Opens after 2 more festivals` once one is held. */
export function opensAfter(remaining: number, held: number): string {
  const n = `${remaining} ${held > 0 ? 'more ' : ''}${remaining === 1 ? term('festival') : term('festivals')}`;
  return `Opens after ${n}`;
}

/**
 * The locked producers shown as goals (reveal slowly): the next in line always, and the one
 * after it once the world holds a quarter of its cost (D261), or always once `seen` already
 * has it (D365: a row that has shown this year stays shown, even past a balance dip).
 */
export function lockedRows(
  state: IdleWorldState,
  content: IdleContent,
  seen: ReadonlySet<string> = new Set(),
): LockedRow[] {
  // D275: unlocking is in any order, so the next two are the first two not yet owned. D301: each
  // open land beside the world has its own next two, shown under its heading.
  const shown = idleShownProducers(state, content);
  return [undefined, ...idleOpenLands(state, content)].flatMap((land) =>
    lockedFor(
      state,
      content,
      shown.filter((p) => p.land === land),
      seen,
    ),
  );
}

/**
 * D419: one list for a line of producers, owned and locked together, always in the content's
 * tier order, so unlocking a higher tier never moves it above a lower one still locked.
 */
export function tierOrder(
  content: IdleContent,
  owned: readonly ProducerRow[],
  locked: readonly LockedRow[],
): ({ kind: 'owned'; row: ProducerRow } | { kind: 'locked'; row: LockedRow })[] {
  const at = (id: string) => content.producers.findIndex((p) => p.id === id);
  return [
    ...owned.map((row) => ({ kind: 'owned' as const, row })),
    ...locked.map((row) => ({ kind: 'locked' as const, row })),
  ].sort((a, b) => at(a.row.id) - at(b.row.id));
}

/** D301: the open pieces of land beside the world, each with its heading, in content order. */
export function landSections(
  state: IdleWorldState,
  content: IdleContent,
): { id: string; name: string }[] {
  const open = idleOpenLands(state, content);
  return content.map.lands
    .filter((l) => open.includes(l.id))
    .map((l) => ({ id: l.id, name: l.name }));
}

/** The locked rows for one line of producers: the world's, or a land's (D261, D275, D301). */
function lockedFor(
  state: IdleWorldState,
  content: IdleContent,
  locked: IdleContent['producers'],
  seen: ReadonlySet<string> = new Set(),
): LockedRow[] {
  const have = state.year.coins;
  if (locked.length === 0) return [];
  const out: LockedRow[] = [];
  const next = locked[0];
  const after = locked[1];
  const show = [next];
  // A producer behind festivals is the last thing shown: only the next one (D261).
  const gated = (id: string) => idleFestivalsNeeded(state, content, id) > 0;
  // D323: the price as the world pays it, from its year-start income after a festival.
  const price = (id: string) => idleUnlockCost(state, content, id);
  if (
    after &&
    next &&
    !gated(next.id) &&
    (seen.has(after.id) || have >= price(after.id) * REVEAL_SHARE)
  )
    show.push(after);
  for (const p of show) {
    if (!p) continue;
    const cost = price(p.id);
    const row: LockedRow = {
      id: p.id,
      name: p.name,
      icon: iconFor(p.id),
      unlockCost: cost,
      payout: p.payout,
      cycleMs: p.cycleMs,
      rate: (p.payout * 1000) / p.cycleMs,
    };
    const festivals = idleFestivalsNeeded(state, content, p.id);
    if (festivals > 0) row.opens = opensAfter(festivals, idleFestivalsThisGeneration(state));
    // D275: either shown producer can be unlocked, in any order.
    else if (have < cost) row.needs = cost - have;
    if (p.land !== undefined) row.land = p.land;
    out.push(row);
  }
  return out;
}

// ---------------------------------------------------------------------------------------
// The barn.

export interface BarnChip {
  /** `Full at 18:40` while playing; `Banked 25m` for a moment after a return. */
  label: string;
  /** The header chip's fixed short form: the time it is full at, `Barn full` or `Banked 4h`. */
  short: string;
  /** When the barn will be full if the player leaves now, on the state's clock. */
  fullAt: number;
  /** The return found the barn full. */
  full: boolean;
  capacity: number;
  /** D362: the share of income the barn keeps past `capacity`, 0 when it keeps none. */
  overflow: number;
}

/** What the last return banked, shown on the chip until `until`. */
export interface Banked {
  away: number;
  capped: boolean;
  until: number;
  /** D324: time lost to a full barn, and its share of `away`. Zero when `capped` is false. */
  lostMs: number;
  lostShare: number;
}

/**
 * The barn holds what the world earns while the player is away (D242). The chip says when it
 * will be full, from its capacity and the last check-in (D258), so the player knows when to
 * come back; just after a return it says what was banked.
 */
export function barnChip(
  state: IdleWorldState,
  content: IdleContent,
  t: number,
  banked?: Banked | null,
): BarnChip {
  const capacity = idleBarnCapacityMs(state, content, idleEffects(state, content, state.lastSeen));
  const fullAt = state.lastSeen + capacity;
  const overflow = idleBarnOverflow(content, state.permanent.barnLevel);
  if (banked && t < banked.until)
    return {
      label: banked.capped
        ? `${cap(term('barn'))} full for ${fmtSpan(banked.lostMs)}: ${pctLost(banked.lostShare)} of time away lost`
        : `Banked ${fmtSpan(banked.away)}`,
      short: banked.capped ? `${cap(term('barn'))} full` : `Banked ${fmtSpan(banked.away)}`,
      full: banked.capped,
      capacity,
      fullAt,
      overflow,
    };
  // D362: a barn past a full day keeps earning at a reduced rate, so the row says so.
  const label =
    overflow > 0
      ? `Full at ${clockAt(fullAt, t)}, keeps ${pctLost(overflow)} after`
      : `Full at ${clockAt(fullAt, t)}`;
  return {
    label,
    short: clockAt(fullAt, t),
    full: false,
    capacity,
    fullAt,
    overflow,
  };
}

export interface BarnOffer {
  level: number;
  /** Hours the next level holds, as words: `4h`. Null at the top level. */
  nextHolds: string | null;
  cost: number | null;
  needs?: number;
  /** D324: coins the next level would have kept from the last absence, when any were lost. */
  wouldHaveKept?: number;
  /** D362: the share of income the next level keeps past its own capacity, 0 for none. */
  overflow: number;
}

export function barnOffer(
  state: IdleWorldState,
  content: IdleContent,
  welcome?: IdleWelcomeBack | null,
): BarnOffer {
  const level = state.permanent.barnLevel;
  const cost = idleBarnUpgradeCost(content, level) ?? null;
  const row = content.barn[level];
  const offer: BarnOffer = {
    level,
    nextHolds: row ? fmtSpan(row.hours * 3_600_000) : null,
    cost,
    overflow: row?.overflow ?? 0,
  };
  if (cost !== null && state.year.coins < cost) offer.needs = cost - state.year.coins;
  if (welcome?.capped && row) {
    const kept = welcomeWaste(welcome, row.hours * 3_600_000, row.overflow ?? 0).wouldHaveKept;
    if (kept > 0) offer.wouldHaveKept = kept;
  }
  return offer;
}

// ---------------------------------------------------------------------------------------
// The welcome back (D242, D245).

/** Shorter than this away and there is no fanfare: the player only glanced off. */
export const WELCOME_MIN_AWAY_MS = 60_000;

export function shouldWelcome(w: IdleWelcomeBack): boolean {
  return w.away >= WELCOME_MIN_AWAY_MS && w.earned > 0;
}

/** A share with no decimals, as `STYLE.md` has it: "40%". */
function pctLost(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/**
 * What a full barn cost over a welcome back's own away time (D324): the rate is `earned` over
 * the time that counted, so it reads as the average the barn was holding while it filled. D362:
 * `w.overflow` is threaded through as what the barn actually kept past capacity; pass an
 * upgrade's own capacity and overflow share as `atCapacityMs`/`atOverflow` to see what that
 * level would have kept instead.
 */
export function welcomeWaste(
  w: IdleWelcomeBack,
  atCapacityMs?: number,
  atOverflow?: number,
): IdleBarnWaste {
  const over = Math.min(w.away, w.capacity);
  const rate = over > 0 ? w.earned / over : 0;
  return idleBarnWaste(w.capacity, rate, w.away, atCapacityMs, w.overflow, atOverflow);
}

/**
 * D362: coins the barn's own overflow share kept that a barn with none would have lost, for
 * the welcome back's waste line. 0 when the barn has no overflow.
 */
function overflowSaved(w: IdleWelcomeBack): number {
  if (w.overflow <= 0) return 0;
  const over = Math.min(w.away, w.capacity);
  const rate = over > 0 ? w.earned / over : 0;
  return idleBarnWaste(w.capacity, rate, w.away, w.capacity, 0, w.overflow).wouldHaveKept;
}

/**
 * The fanfare's words: what was banked, the hours it covered and, when the barn filled, how
 * much of the night was lost (D324), as "4h banked. Barn full for 2h: 50% of the night lost."
 * D362: when the barn's overflow kept some of that anyway, a line says how much.
 */
export function welcomeLines(w: IdleWelcomeBack): { title: string; amount: string; line: string } {
  const banked = `${fmtSpan(w.away)} banked.`;
  const title = 'Welcome back';
  const amount = `+${coins(w.earned)}`;
  if (!w.capped) return { title, amount, line: banked };
  const waste = welcomeWaste(w);
  const saved = overflowSaved(w);
  const savedText = saved > 0 ? ` The ${term('barn')} kept ${coins(saved)} of it.` : '';
  const line = `${banked} ${cap(term('barn'))} full for ${fmtSpan(waste.lostMs)}: ${pctLost(waste.lostShare)} of time away lost.${savedText}`;
  return { title, amount, line };
}

/** D254: lucky finds missed while here, paid into the barn. Null when there were none. */
export function welcomeFinds(w: { missedFinds?: number; missedCoins?: number }): string | null {
  const n = w.missedFinds ?? 0;
  if (n <= 0) return null;
  const what = (n === 1 ? term('luckyFind') : term('luckyFinds')).toLowerCase();
  return `${n} ${what} paid into the ${term('barn')}: ${coins(w.missedCoins ?? 0)}.`;
}

/**
 * D313: the visitor who called while away, named, or how many when there was more than one.
 * Null when there were none. `The vet called while you were away.`, `Two visitors called while
 * you were away.`
 */
export function welcomeVisitors(
  w: { missedVisitors?: string[] },
  content: IdleContent,
): string | null {
  const ids = w.missedVisitors ?? [];
  if (ids.length === 0) return null;
  if (ids.length === 1) {
    const name =
      content.bonuses.visitors.kinds.find((k) => k.id === ids[0])?.name ??
      `A ${content.terms.visitor}`;
    return `${name} called.`;
  }
  const n = ids.length === 2 ? 'Two' : String(ids.length);
  return `${n} ${content.terms.visitors} called.`;
}

/** D258: when to come back. `Barn full again at 18:40.` */
export function welcomeNext(fullAt: number, t: number): string {
  return `${cap(term('barn'))} full again at ${clockAt(fullAt, t)}.`;
}

// ---------------------------------------------------------------------------------------
// Tips (D244: each when the thing first appears; D245's carrots tip; D261: year one only,
// once each, and one on spending Heritage after the first festival).

// D276: Heritage to spend has its own line on the World tab, so it has no tip.
export type TipId = 'tap_wheat' | 'hire_hand' | 'carrots' | 'heritage' | 'arrange';

/** The tips in the order they can come. */
export const TIP_ORDER: readonly TipId[] = ['tap_wheat', 'hire_hand', 'carrots'];

/** The tip's words, with the producers named from the content. */
export function tipText(id: TipId, content: IdleContent): string {
  // D359: the theme's own names and words, and nothing that reads as world on another theme.
  const first = content.producers[0]?.name.toLowerCase() ?? '';
  const second = content.producers[1]?.name.toLowerCase() ?? '';
  if (id === 'tap_wheat') return `Tap the ${first} to collect. Tap again when the bar is full.`;
  if (id === 'hire_hand')
    return `Hire ${aTerm(content.terms.hand)} to keep the ${first} going while you are away.`;
  if (id === 'heritage')
    return `You have ${term('heritage')} to spend on the ${term('tabFestival')} tab.`;
  return `Unlock ${second}. It pays far more than ${first}.`;
}

/**
 * Whether the thing a tip is about is on screen now, seen or not. A tip shown stays while
 * this holds, and goes when it stops.
 */
export function tipApplies(id: TipId, state: IdleWorldState, content: IdleContent): boolean {
  const first = content.producers[0];
  const second = content.producers[1];
  const fp = first ? state.year.producers[first.id] : undefined;
  if (id === 'heritage')
    return (
      state.permanent.festivals.length > 0 &&
      state.permanent.heritage > 0 &&
      Object.keys(state.permanent.perks).length === 0
    );
  // The rest teach the first year only (D261).
  if (state.year.number !== 1 || state.permanent.festivals.length > 0) return false;
  if (id === 'tap_wheat') return !!fp && !fp.hand && !fp.running && state.year.earned === 0;
  if (id === 'hire_hand') {
    if (!first || !fp || fp.hand) return false;
    const effects = idleEffects(state, content, state.advancedTo);
    return state.year.coins >= idleHandCost(state, content, first.id, effects);
  }
  return !!second && !state.year.producers[second.id] && state.year.coins >= second.unlockCost;
}

/** The tip to show now, or null. `seen` holds the tips already shown: each shows once. */
export function tipFor(
  state: IdleWorldState,
  content: IdleContent,
  seen: ReadonlySet<TipId>,
): TipId | null {
  return TIP_ORDER.find((id) => !seen.has(id) && tipApplies(id, state, content)) ?? null;
}

// ---------------------------------------------------------------------------------------
// The region (D244).

/** The goal's words: how far the region is, how long is left, and what meeting it pays. */
export function goalLines(p: IdleRegionView, t: number): { progress: string; reward: string } {
  const g = p.goal;
  const left = fmtSpan(Math.max(0, g.weekEndsAt - t));
  return {
    progress: `${coins(g.earned)} of ${coins(g.target)}. ${left} left.`,
    reward: g.met
      ? `Met. Every ${term('world')} here earns ${g.factor}× next week.`
      : `Meet it and every ${term('world')} here earns ${g.factor}× next week.`,
  };
}

// ---------------------------------------------------------------------------------------
// The Almanac: upgrades and goals (D244).

/** An effect as the player reads it. */
export interface EffectSpec {
  stat: 'speed' | 'value' | 'tap' | 'barn' | 'hand' | 'upgrade' | 'find';
  producer?: string | undefined;
  /** D454: every producer of this group instead of one. */
  group?: 'crop' | 'animal' | undefined;
  factor: number;
  /** A synergy (D251): the effect comes once for every `every` of `producer` owned. */
  per?: { producer: string; every: number } | undefined;
}

/** Twice and three times read better than x2 and x3. */
const TIMES: Record<string, string> = { '2': 'twice', '3': 'three times' };

/** A rise as the player reads it: "50% more", "twice as much", "x4.5". */
function rise(f: number, more: string, as: string, big = ''): string {
  if (f < 2) return `${Math.round((f - 1) * 100)}% ${more}`;
  const t = TIMES[String(f)];
  return t ? `${t} as ${as}` : `x${f}${big}`;
}

/** A cut as the player reads it: "50% less". */
function cut(f: number): string {
  return `${Math.round((1 - f) * 100)}% less`;
}

/**
 * What an effect does, in plain words (STYLE.md): "Wheat pays 50% more", "Carrots work 25%
 * faster", "Taps pay twice as much". Every upgrade's words come from here, never from its
 * content name.
 */
export function effectText(content: IdleContent, e: EffectSpec): string {
  const base = effectBase(content, e);
  if (!e.per) return base;
  const name = content.producers.find((p) => p.id === e.per?.producer)?.name ?? e.per.producer;
  return `${base} for every ${e.per.every} ${name.toLowerCase()}`;
}

/** D359: `a hand`, `an engineer`: a term with its article. */
export function aTerm(word: string): string {
  return `${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}`;
}

function effectBase(content: IdleContent, e: EffectSpec): string {
  // D359: no theme's own word for all its producers, so the whole place: `Everything on the
  // world`, `Everything on the campus`.
  // D454: a group (`crops`, `animals`) reads as its theme word, always plural.
  const who = e.group
    ? cap(content.groups?.[e.group] ?? e.group)
    : e.producer
      ? (content.producers.find((p) => p.id === e.producer)?.name ?? e.producer)
      : `Everything on the ${term('world')}`;
  // Carrots pay, wheat pays: a name that ends in s is a plural.
  const many = !!e.group || (!!e.producer && who.endsWith('s'));
  const f = e.factor;
  switch (e.stat) {
    case 'value':
      return `${who} ${many ? 'pay' : 'pays'} ${rise(f, 'more', 'much')}`;
    case 'speed':
      return `${who} ${many ? 'work' : 'works'} ${rise(f, 'faster', 'fast', ' faster')}`;
    case 'tap': {
      // D277: a tap pays on a ripe crop and speeds a hand's bar, so the upgrade lifts both.
      // D391: two plain sentences. The sim multiplies the share of a bar one tap takes off a
      // producer that a hand runs (idleHandTapShare), so a tap moves that bar on further.
      const on = e.producer || e.group ? ` on ${who.toLowerCase()}` : '';
      const far = rise(f, 'further', 'far', ' as far');
      return `Taps${on} pay ${rise(f, 'more', 'much')}. A tap${on} with ${aTerm(term('hand'))} moves the bar on ${far}.`;
    }
    case 'barn':
      return `The ${term('barn')} holds ${rise(f, 'longer', 'long', ' as long')}`;
    case 'hand':
      return `${cap(term('hands'))}${e.producer ? ` for ${who.toLowerCase()}` : ''} cost ${cut(f)}`;
    case 'upgrade':
      return `${term('almanac')} upgrades cost ${cut(f)}`;
    case 'find':
      return `${cap(term('luckyFinds'))} pay ${rise(f, 'more', 'much')}`;
  }
}

/** D295: a multiplier's total as the player reads it: "+52%", "x2.3", "x21.6", "49% less". */
function factorTotal(stat: EffectSpec['stat'], f: number): string {
  if (stat === 'hand' || stat === 'upgrade') return cut(f);
  // One rule everywhere: "x" from twice upwards, "+N%" below.
  if (f < 2) return `+${Math.round((f - 1) * 100)}%`;
  return f < 100 ? `×${Number(f.toFixed(1))}` : `×${num(Math.round(f))}`;
}

/** D295: one total as words: "6,000c", "2 festivals sooner", "44% kept". */
function totalWords(t: IdleTotal, n: number): string {
  switch (t.kind) {
    case 'factor':
      return factorTotal(t.stat, n);
    case 'coins':
      return coins(n);
    case 'festivals':
      return `${num(n)} ${n === 1 ? term('festival') : term('festivals')} sooner`;
    case 'crops':
      return `${num(n)} ${n === 1 ? 'crop' : 'crops'}`;
    case 'barn_hours':
      return `${num(n)} hours`;
    case 'growth_cut':
      return `${Number(n.toFixed(4))} lower`;
    case 'share':
      return `${Math.round(n * 100)}% kept`;
  }
}

/**
 * D295: what a perk, upgrade or trait adds up to, from the sim's total: "Now +52%, next +75%",
 * or "Now 2 festivals sooner" at a top level. Null while it adds nothing yet, as the step then
 * says it all, and for a one-off with nothing to add up.
 */
export function totalText(t: IdleTotal | null): string | null {
  if (!t) return null;
  if (t.now === (t.kind === 'factor' ? 1 : 0)) return null;
  const now = `Now ${totalWords(t, t.now)}`;
  return t.next === null ? now : `${now}, next ${totalWords(t, t.next)}`;
}

/** The Almanac shows two or three upgrades at a time (D244): the cheapest on show. */
export const UPGRADES_ON_SHOW = 3;

export interface UpgradeCard {
  id: string;
  text: string;
  /** D431: the icon of the producer it touches, or a spark for every producer. */
  icon: string;
  /** D295: what this year's upgrades add up to for what it changes, now and once bought. */
  total?: string;
  cost: number;
  needs?: number;
}

/** The upgrades on show, cheapest first, and the ones bought this year. */
export function upgradeCards(
  state: IdleWorldState,
  content: IdleContent,
  visible: IdleUpgradeT[],
): {
  onShow: UpgradeCard[];
  bought: UpgradeCard[];
  /** D251: the next page behind festivals, as a quiet goal. */
  next: string | null;
} {
  const have = state.year.coins;
  const effects = idleEffects(state, content, state.advancedTo);
  const card = (u: IdleUpgradeT): UpgradeCard => {
    const cost = idleUpgradeCost(state, content, u.id, effects);
    const c: UpgradeCard = {
      id: u.id,
      text: effectText(content, u.effect),
      icon: u.effect.producer ? iconFor(u.effect.producer) : 'sparkles',
      cost,
    };
    const total = totalText(idleUpgradeTotal(state, content, u.id));
    if (total) c.total = total;
    if (have < cost) c.needs = cost - have;
    return c;
  };
  const onShow = visible
    .filter((u) => u.choice === undefined)
    .map(card)
    .sort((a, b) => a.cost - b.cost)
    .slice(0, UPGRADES_ON_SHOW);
  const boughtUps = state.year.upgrades
    .map((id) => content.upgrades.find((u) => u.id === id))
    .filter((u): u is IdleUpgradeT => !!u);
  // D391: an upgrade that reads the same as one already bought shows once, as "Level 2".
  const bought: UpgradeCard[] = [];
  const seen = new Map<string, { card: UpgradeCard; n: number; base: string }>();
  for (const u of boughtUps) {
    const base = effectText(content, u.effect);
    const hit = seen.get(base);
    if (hit) {
      hit.n += 1;
      hit.card.text = `${base} (Level ${hit.n})`;
      hit.card.cost += u.cost;
    } else {
      const c: UpgradeCard = {
        id: u.id,
        text: base,
        icon: u.effect.producer ? iconFor(u.effect.producer) : 'sparkles',
        cost: u.cost,
      };
      seen.set(base, { card: c, n: 1, base });
      bought.push(c);
    }
  }
  // The next upgrade behind festivals, the nearest only.
  const held = idleFestivalsThisGeneration(state);
  const gated = content.upgrades
    .filter((u) => u.needsFestivals !== undefined && u.needsFestivals > held)
    .filter((u) => u.needsPerk === undefined || !!state.permanent.perks[u.needsPerk])
    .sort((a, b) => (a.needsFestivals ?? 0) - (b.needsFestivals ?? 0))[0];
  // D264: a long-summer page opens after hours of the year; the nearest is the next goal.
  const yearH = (state.advancedTo - state.year.startedAt) / 3_600_000;
  const summer = content.upgrades
    .filter((u) => u.needsYearHours !== undefined && u.needsYearHours > yearH)
    .filter((u) => u.needsFestivals === undefined || u.needsFestivals <= held)
    .filter((u) => u.needsProducer === undefined || !!state.year.producers[u.needsProducer])
    .sort((a, b) => (a.needsYearHours ?? 0) - (b.needsYearHours ?? 0))[0];
  const next = summer
    ? `More upgrades after ${summer.needsYearHours} hours this year`
    : gated
      ? `More upgrades: ${opensAfter((gated.needsFestivals ?? 0) - held, held).toLowerCase()}`
      : null;
  return {
    onShow,
    bought,
    next,
  };
}

export interface GoalRow {
  id: string;
  /** The tier on show, its target in the words: `Own 120 wheat`. */
  name: string;
  /** What meeting the tier on show pays: "Every crop pays 3% more this year". */
  reward: string;
  /** Every tier is met. */
  met: boolean;
  /** The tier on show's progress, 0 to 1. */
  progress: number;
  /** D334: the tier on show, how many there are and how many are met. One tier: no tier line. */
  tier: number;
  tiers: number;
  metTiers: number;
  /** D334: a long goal, kept through festivals until the world is passed on. */
  long: boolean;
}

/** D334: a goal's name with a tier's target: `Own 120 wheat`, `Earn 1.0e18c this year`. */
export function goalName(goal: IdleGoalT, target: number): string {
  const money = goal.kind === 'earn' || goal.kind === 'lifetime';
  return goal.name.replace('{n}', money ? coins(target) : num(target));
}

/** D334: a met goal tier, from its key (`own_25_wheat@2`), as a name and what it pays. */
export function goalMetText(
  content: IdleContent,
  state: IdleWorldState,
  key: string,
): { name: string; reward: string } | null {
  const { id, tier } = idleGoalParse(key);
  const goal = [...content.goals, ...content.longGoals].find((g) => g.id === id);
  if (!goal) return null;
  return {
    name: goalName(goal, idleGoalTarget(state, content, goal, tier)),
    reward: `${effectText(content, idleGoalReward(content, goal, tier))} ${idleGoalIsLong(goal) ? `until you ${lower(term('passOn'))}` : 'this year'}`,
  };
}

/** D335: met keys the content still knows a goal for, in order; never a raw id left in play. */
export function knownGoalKeys(content: IdleContent, keys: string[]): string[] {
  const ids = new Set([...content.goals, ...content.longGoals].map((g) => g.id));
  return keys.filter((k) => ids.has(idleGoalParse(k).id));
}

export interface WelcomeGoalsSummary {
  /** Up to three goal tiers met, by their plain name. */
  shown: string[];
  /** How many more were met than are shown. */
  more: number;
}

/**
 * D335: the welcome back's goals met while away, named plainly and capped at three, with how
 * many more to point at the Almanac for. A key the content has no goal for is dropped rather
 * than shown raw.
 */
export function welcomeGoalsSummary(
  content: IdleContent,
  state: IdleWorldState,
  keys: string[],
): WelcomeGoalsSummary {
  const names = keys
    .map((k) => goalMetText(content, state, k)?.name)
    .filter((n): n is string => !!n);
  return { shown: names.slice(0, 3), more: Math.max(0, names.length - 3) };
}

/** The goals, nearest first, met ones last; long goals keep their own order after. */
export function goalRows(content: IdleContent, goals: IdleGoalView[]): GoalRow[] {
  return goals
    .map((g) => ({
      id: g.goal.id,
      name: goalName(g.goal, g.target),
      reward: `${effectText(content, idleGoalReward(content, g.goal, g.tier))} ${g.long ? `until you ${lower(term('passOn'))}` : 'this year'}`,
      met: g.met,
      progress: Math.max(0, Math.min(1, g.progress)),
      tier: g.tier,
      tiers: g.tiers,
      metTiers: g.metTiers,
      long: g.long,
    }))
    .sort(
      (a, b) =>
        Number(a.long) - Number(b.long) || Number(a.met) - Number(b.met) || b.progress - a.progress,
    );
}

// ---------------------------------------------------------------------------------------
// The harvest festival (D244, D245, D246).

/** A mark's id as words: `standing_stone` -> `standing stone`. */
export const markName = (id: string): string => id.replace(/_/g, ' ');

/** D470: a mark name with its article: `a hedgerow`; a plural name (`standing stones`) takes none. */
export function markWithArticle(name: string): string {
  const n = name.toLowerCase();
  return n.endsWith('s') ? n : aTerm(n);
}

/** D470: what a mark does, said plainly: `A hedgerow stays on your map.` */
export function markStaysLine(name: string): string {
  const n = name.toLowerCase();
  return `${cap(markWithArticle(n))} ${n.endsWith('s') ? 'stay' : 'stays'} on your map.`;
}

/** D470: the year-end line: `Your farm gains a hedgerow. It stays on your map.` */
export function markGainLine(name: string, world: string): string {
  const n = name.toLowerCase();
  return `Your ${world} gains ${markWithArticle(n)}. ${n.endsWith('s') ? 'They stay' : 'It stays'} on your map.`;
}

/** D442: a mark's icon, for the year-end grid. */
export const markIcon = (id: string): string => MARK_ICON[id] ?? 'landmark';

/** An income multiplier as a bonus: 1.4 -> `+40%`. */
export const bonusText = (factor: number): string => `+${Math.round((factor - 1) * 100)}%`;

export interface FestivalView {
  available: boolean;
  /** `Earn 1Bc this year to hold it`. */
  goal: string;
  /** How far the year has come towards it, 0 to 1. */
  progress: number;
  heritage: number;
  /** `+40% income, rising to +140%`, or `All income +100% after it` the first time. */
  bonus: string;
  mark: string;
  /** D252: `2 of it from this year's goals`, or null when goals added none. */
  fromGoals: string | null;
}

export function festivalView(
  content: IdleContent,
  state: IdleWorldState,
  p: {
    available: boolean;
    heritage: number;
    bonusNow: number;
    bonusAfter: number;
    mark: string;
    needs?: number;
    fromGoals?: number;
  },
): FestivalView {
  // D256, D263: what the year must still earn, from the sim's own check.
  const needs = p.needs ?? Math.max(0, content.festival.minYearEarned - state.year.earned);
  const earned = state.year.earned;
  return {
    available: p.available,
    goal: `Needs ${coins(needs)} more this year`,
    progress: needs <= 0 ? 1 : Math.min(1, earned / (earned + needs)),
    fromGoals: p.fromGoals && p.fromGoals >= 1 ? `${num(p.fromGoals)} from goals` : null,
    heritage: p.heritage,
    bonus:
      p.bonusNow > 1
        ? `${bonusText(p.bonusNow)} income, rising to ${bonusText(p.bonusAfter)}`
        : `${bonusText(p.bonusAfter)} income from next year`,
    mark: markName(p.mark),
  };
}

export interface HeritageGrowth {
  /** `+400`, the gain itself: always the big figure, however small a share it is (D357). */
  gain: string;
  /** `12% more (33K to 47.8K)`, or `Under 1% more than you hold (1.0000M to 1.0004M)`. */
  detail: string;
}

/**
 * D357: Heritage as holding now would leave it, led by the gain so a tiny one is not lost in
 * rounding, then the figure before and after at a precision that tells them apart.
 */
export function heritageGrowthText(before: number, after: number): HeritageGrowth {
  const gain = Math.max(0, after - before);
  const pct = before > 0 ? gain / before : gain > 0 ? Infinity : 0;
  const small = pct < 0.01;
  let range: string;
  if (small) {
    // A gain this small needs more figures before the two numbers read as different at all,
    // up to a point past which the words beside them already say it.
    let decimals = 2;
    let b = fmtBigFixed(before, decimals);
    let a = fmtBigFixed(after, decimals);
    while (b === a && gain > 0 && decimals < 6) {
      decimals += 1;
      b = fmtBigFixed(before, decimals);
      a = fmtBigFixed(after, decimals);
    }
    range = `${b} to ${a}`;
  } else {
    range = `${num(before)} to ${num(after)}`;
  }
  const share = small ? 'Under 1% more' : `${Math.floor(pct * 100)}% more`;
  return {
    gain: `+${num(gain)}`,
    detail: `${share} (${range})`,
  };
}

/** What a perk does, in plain words. The two ladders (D367) read as their theme names them. */
export function perkText(content: IdleContent, perk: IdlePerkT): string {
  if (perk.effect) return effectText(content, perk.effect);
  if (perk.startCoins) return `Each year starts with ${coins(perk.startCoins)} more`;
  return perk.name;
}

/**
 * D367: why a perk cannot be bought yet, or undefined when it can: "Opens after 1 more
 * festival", or "Needs potatoes first" for a hand whose crop is not owned.
 */
export function perkLockText(
  content: IdleContent,
  state: IdleWorldState,
  perk: IdlePerkT,
): string | undefined {
  const festivals = idlePerkFestivalsNeeded(state, perk);
  if (festivals > 0) return opensAfter(festivals, idleFestivalsThisGeneration(state));
  const first = idlePerkNeedsPerk(state, perk);
  if (first === undefined) return undefined;
  const k = content.perks.find((x) => x.id === first);
  const crop = k?.startProducers?.[0];
  const name = content.producers.find((p) => p.id === crop)?.name;
  return name ? `Needs ${name.toLowerCase()} first` : 'Needs another perk first';
}

export interface PerkRow {
  id: string;
  text: string;
  /** D295: what it adds up to now and after the next level; null when there is nothing to add. */
  total: string | null;
  level: number;
  /** Null for a perk with no top level (D250). */
  maxLevel: number | null;
  /** Heritage for the next level; null at the top level, and while `locked` is set. */
  cost: number | null;
  /** Heritage short. */
  needs?: number;
  /** D362: festivals still to hold this generation before it opens; absent once it has. */
  festivalsNeeded?: number;
  /** D367: why it cannot be bought yet ("Opens after 1 more festival"); absent when it can. */
  locked?: string;
}

export function perkRows(
  content: IdleContent,
  state: IdleWorldState,
  costOf: (perk: IdlePerkT, level: number) => number,
): PerkRow[] {
  const have = state.permanent.heritage;
  return content.perks.map((perk) => {
    const level = state.permanent.perks[perk.id] ?? 0;
    const top = perk.maxLevel !== undefined && level >= perk.maxLevel;
    const festivalsNeeded = top ? 0 : idlePerkFestivalsNeeded(state, perk);
    const locked = top ? undefined : perkLockText(content, state, perk);
    const cost = top || locked !== undefined ? null : costOf(perk, level);
    const row: PerkRow = {
      id: perk.id,
      text: perkText(content, perk),
      total: totalText(idlePerkTotal(state, content, perk.id)),
      level,
      // D323: a world that bought past a top set later keeps its levels, shown as the top.
      maxLevel: perk.maxLevel === undefined ? null : Math.max(perk.maxLevel, level),
      cost,
    };
    if (festivalsNeeded > 0) row.festivalsNeeded = festivalsNeeded;
    if (locked !== undefined) row.locked = locked;
    else if (cost !== null && have < cost) row.needs = cost - have;
    return row;
  });
}

// ---------------------------------------------------------------------------------------
// Bonuses (D244: never punishments): a market day, a lucky find, good weather.

export interface ActiveBonus {
  factor: number;
  until: number;
}

/** The market day and the good weather in force at `t`, if any. */
export function activeBonuses(
  state: IdleWorldState,
  t: number,
): { market?: ActiveBonus; weather?: ActiveBonus } {
  const out: { market?: ActiveBonus; weather?: ActiveBonus } = {};
  for (const b of state.bonuses) {
    if (t < b.from || t >= b.until) continue;
    if (b.source === 'market_day') out.market = { factor: b.factor, until: b.until };
    if (b.source === 'good_weather') out.weather = { factor: b.factor, until: b.until };
  }
  return out;
}

/** `Market day: double income for 1m 30s`, or the theme's own name for the offer. */
export function marketOfferText(factor: number, durationMs: number): string {
  return `${term('market')}: ${incomeWhat(factor)} for ${fmtSpan(durationMs)}`;
}

/** What a market day does to income: `double income`, `50% more income`, `3 times the income`. */
export function incomeWhat(factor: number): string {
  return factor === 2
    ? 'double income'
    : factor < 2
      ? `${Math.round((factor - 1) * 100)}% more income`
      : `${factor} times the income`;
}

/** `Good weather: 25% more income for 10m`, or the theme's own name for it. */
export function weatherText(factor: number, durationMs: number): string {
  return `${term('weather')}: ${Math.round((factor - 1) * 100)}% more income for ${fmtSpan(durationMs)}`;
}

/**
 * Where a lucky find lies on the map, as a share of the map's width and height: a spot on
 * the world picked from its id, so it stays put while it is there.
 */
export function findSpot(id: string): { left: number; top: number } {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return { left: 0.3 + ((h % 1000) / 1000) * 0.4, top: 0.4 + (((h >>> 10) % 1000) / 1000) * 0.25 };
}

// ---------------------------------------------------------------------------------------
// D258: the Next line under the header.

export interface NextLine {
  kind: 'festival_ready' | 'festival' | 'goal' | 'heritage' | 'upgrade' | 'order';
  /** `Own 25 wheat`, `Earn 1Bc this year for the festival`, `The harvest festival is ready`. */
  text: string;
  /**
   * D457: a second line in small type under the text ("Almanac - Tapping - 480Kc"). A strip with
   * one wraps its text onto up to two lines instead of cutting it.
   */
  detail?: string;
  /** 0 to 1. */
  progress: number;
  /** Where it is met: the tab, and the row to bring into view there. */
  tab: 'world' | 'festival' | 'almanac' | 'region';
  testId?: string;
}

/**
 * The nearest thing to aim for: the festival once it can be held; otherwise the unmet goal
 * furthest along, or the festival when the year is further along towards it than any goal.
 */
export function nextLine(state: IdleWorldState, content: IdleContent): NextLine {
  if (idleFestivalAvailable(state, content)) {
    // D299: the gold bar only at the good moment; before it, the festival is still growing.
    const advice = festivalAdvice(state, content, state.advancedTo);
    return {
      kind: advice.advice === 'hold' ? 'festival_ready' : 'festival',
      text: advice.short,
      progress: advice.progress,
      tab: 'festival',
    };
  }
  const needs = idleFestivalPreview(state, content).needs;
  const earned = state.year.earned;
  const festival: NextLine = {
    kind: 'festival',
    text: `Earn ${coins(needs)} more for the ${term('festival')}`,
    progress: needs <= 0 ? 1 : Math.min(1, earned / (earned + needs)),
    tab: 'festival',
  };
  // A goal complete by its progress is met at the next action or check-in (D248): never next.
  const best = idleGoals(state, content)
    .filter((g) => !g.met && !g.long && g.progress < 1)
    .sort((a, b) => b.progress - a.progress)[0];
  if (!best || festival.progress > best.progress) return festival;
  const g = best.goal;
  const line: NextLine = {
    kind: 'goal',
    text: goalName(g, best.target),
    progress: Math.max(0, Math.min(1, best.progress)),
    tab: 'world',
  };
  const ys = state.year.producers;
  if (g.kind === 'own' && g.producer)
    line.testId = ys[g.producer] ? `producer-${g.producer}` : `locked-${g.producer}`;
  else if (g.kind === 'chain' && g.chain) line.testId = `chain-${g.chain}`;
  else if (g.kind === 'hands') {
    const p = content.producers.find((x) => ys[x.id] && !ys[x.id]!.hand);
    if (p) line.testId = `producer-${p.id}`;
  }
  return line;
}

// ---------------------------------------------------------------------------------------
// D257: generations and passing the world on.

/**
 * The year as this generation counts it: one more than the festivals it has held, so a world
 * with a festival behind it is in year 2 however its year number was stored, and passing on
 * starts a generation at year 1.
 */
export function yearOfGeneration(state: IdleWorldState): number {
  return Math.max(1, idleFestivalsThisGeneration(state) + 1);
}

/**
 * D431: the header's line beside the world name, `Year 5 · Reign 1`, the generation always shown (D439) in
 * the theme's own word, written out in full.
 */
export function yearHeader(state: IdleWorldState): string {
  const g = idleGeneration(state);
  const y = yearOfGeneration(state);
  return `Year ${y} · ${cap(term('generation'))} ${g}`;
}

/** D325: passing the world on, in plain words: what goes, what stays, and what it pays. */
export interface PassOnText {
  lose: string;
  keep: string;
  /** `Pays 5 Lineage: 4 from Heritage, 1 from play.` */
  pays: string;
  /** D371: the same two, one item each, for a list. */
  loseList: string[];
  keepList: string[];
  /** The meter and what fills it. */
  meter: string;
  /** What Lineage is for. */
  use: string;
  /** D477: what the next level of the Heritage trait pays; null when no trait pays it. */
  buys: string | null;
}

/** D477: a trait's name for a line of copy, from its id: "harvest_lore" is "Harvest lore". */
export function traitTitle(id: string): string {
  const w = id.replace(/_/g, ' ');
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/** D477: the Heritage trait and what its next level multiplies festival Heritage by. */
export function heritageTraitNext(
  content: IdleContent,
  state: IdleWorldState,
): { name: string; factor: number } | null {
  const t = content.lineage.traits.find((x) => x.heritageGain);
  if (!t?.heritageGain) return null;
  const level = state.permanent.lineage?.traits[t.id] ?? 0;
  return { name: traitTitle(t.id), factor: 1 + t.heritageGain * (level + 1) };
}

export function passOnText(
  p: {
    fromHeritage: number;
    fromPlay: number;
    meter: number;
  },
  lore: { name: string; factor: number } | null = null,
): PassOnText {
  const total = p.fromHeritage + p.fromPlay;
  const heritage = term('heritage');
  const lineage = term('lineage');
  const loseList = [heritage, 'perks', `the ${term('barn')}`, 'this year'];
  const keepList = [`${term('festival')} marks`, 'traits', lineage];
  const join = (l: string[]) => `${l.slice(0, -1).join(', ')} and ${l[l.length - 1]}`;
  return {
    loseList,
    keepList,
    lose: `You lose: ${join(loseList)}.`,
    keep: `You keep: ${join(keepList)}.`,
    pays: `Pays ${num(total)} ${lineage}: ${num(p.fromHeritage)} from ${heritage}, ${num(p.fromPlay)} from play.`,
    meter: `${lineage} meter: ${(Math.floor(p.meter * 100) / 100).toFixed(2)}. Orders, visitors, chains and ${term('festivals')} fill it. Each whole point pays out when you ${lower(term('passOn'))}.`,
    use: `${lineage} buys traits. Traits last for every later ${term('generation')}.`,
    buys: lore
      ? `Spend ${lineage} on ${lore.name}: ${lower(term('festivals'))} pay ${Number(lore.factor.toFixed(1))}× the ${heritage}.`
      : null,
  };
}

/** D477: "Now x4.5, next x8" for a trait that multiplies festival Heritage; null before level 1. */
function heritageGainTotal(now: number, next: number, top: number | null): string | null {
  if (now === 1) return null;
  const a = `Now ${factorTotal('value', now)}`;
  return top !== null && now >= next ? a : `${a}, next ${factorTotal('value', next)}`;
}

export interface TraitRow {
  id: string;
  text: string;
  /** D295: what it adds up to now and after the next level; null when there is nothing to add. */
  total: string | null;
  level: number;
  /** Null for a trait with no top level (D264). */
  maxLevel: number | null;
  /** Lineage for the next level; null at the top level. */
  cost: number | null;
  needs?: number;
}

export function traitRows(
  content: IdleContent,
  state: IdleWorldState,
  costOf: (trait: IdleContent['lineage']['traits'][number], level: number) => number,
): TraitRow[] {
  const have = state.permanent.lineage?.points ?? 0;
  return content.lineage.traits.map((t) => {
    const level = state.permanent.lineage?.traits[t.id] ?? 0;
    // A trait with no top level (D264) can always be bought again.
    const top = t.maxLevel ?? null;
    const cost = top !== null && level >= top ? null : costOf(t, level);
    const gain = t.heritageGain
      ? heritageGainTotal(1 + t.heritageGain * level, 1 + t.heritageGain * (level + 1), top)
      : null;
    const total = gain ?? totalText(idleTraitTotal(state, content, t.id));
    const row: TraitRow = { id: t.id, text: t.name, total, level, maxLevel: top, cost };
    if (cost !== null && have < cost) row.needs = cost - have;
    return row;
  });
}

// ---------------------------------------------------------------------------------------
// D264: whether now is a good time for the festival, in plain words.

export interface FestivalAdviceText {
  advice: 'not_ready' | 'grow' | 'hold';
  /** D371, for the Next line: "Not yet. Wait about 1h 30m for a good launch". */
  short: string;
  /** For the Festival tab; null when it cannot be held yet. */
  long: string | null;
  /** D371: how far the wait has come, 0 to 1, so the bar and the wait agree. */
  progress: number;
}

export function festivalAdvice(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): FestivalAdviceText {
  const a = idleFestivalAdvice(state, content, now);
  if (a.advice === 'not_ready') return { advice: a.advice, short: '', long: null, progress: 0 };
  // D371: one plain line, never the lift figure. Good now, or how long to wait.
  if (a.advice === 'hold') {
    const line = `Good time for the ${term('festival')}`;
    return { advice: a.advice, short: line, long: `${line}.`, progress: 1 };
  }
  // Grow: how long it must still run, as time (D357), to be worth holding.
  const preview = idleFestivalPreview(state, content);
  // D385: the earnings are met and only time is left: say how long, with a bar by time.
  if (preview.heritage >= preview.readyHeritage && preview.readyInMs > 0) {
    const line = `Ready in ${fmtSpan(Math.max(1000, preview.readyInMs))}`;
    return {
      advice: a.advice,
      short: line,
      long: `${line}.`,
      progress: Math.max(0, Math.min(0.999, 1 - preview.readyInMs / Math.max(1, preview.floorMs))),
    };
  }
  const goals = preview.fromGoals;
  const more = idleFestivalCoinsFor(state, content, Math.max(1, a.readyHeritage - goals));
  const minWait = preview.readyInMs;
  // D333: a year never runs past `maxYearMs` before the festival pays the ready lift, so that
  // moment is the latest the wait can run to; sooner, if the year's current earning rate gets
  // there first.
  const topUpAt = state.year.startedAt + content.festival.ready.maxYearMs;
  const rate = idleIncome(state, content, now, true);
  const etaMs = rate > 0 ? Math.ceil((more / rate) * 1000) : Infinity;
  const topUpMs = Math.max(0, topUpAt - now);
  // D476: never sooner than the year's floor, which the earnings alone cannot shorten.
  const waitMs = Math.max(minWait, more <= 0 ? 0 : Math.min(etaMs, topUpMs));
  const line = `Wait about ${fmtSpan(Math.max(1000, waitMs))} for a better ${term('festival')}`;
  return {
    advice: a.advice,
    short: line,
    long: `${line}.`,
    progress: a.yearMs / (a.yearMs + Math.max(1, waitMs)),
  };
}

/** D322: an income lift as the player reads it, cut down, never rounded up: `x1.25`, `x1.4`. */
export function liftText(f: number): string {
  const step = f < 10 ? 100 : 10;
  return `x${Math.floor(Math.max(1, f) * step) / step}`;
}

/**
 * D276: Heritage to spend that buys a perk now, for the World tab's line (D460: no longer a
 * badge). Null when no perk is affordable.
 */
export function heritageToSpend(
  state: IdleWorldState,
  content: IdleContent,
  costOf: (perk: IdlePerkT, level: number) => number,
): number | null {
  const rows = perkRows(content, state, costOf);
  return rows.some((r) => r.cost !== null && r.needs === undefined)
    ? state.permanent.heritage
    : null;
}

/**
 * D336: ids of the not-maxed perks whose next level is affordable now. The "Heritage to
 * spend" bar is worth showing only while this is non-empty (a full level of the cheapest
 * perk still open), and it is what the bar's dismissal is compared against: it returns once
 * an id here was not affordable when the player last dismissed it.
 */
export function affordablePerkIds(
  state: IdleWorldState,
  content: IdleContent,
  costOf: (perk: IdlePerkT, level: number) => number,
): string[] {
  return perkRows(content, state, costOf)
    .filter((r) => r.cost !== null && r.needs === undefined)
    .map((r) => r.id);
}

/**
 * D285: the World tab's one prompt bar. The most valuable next action, in a fixed order: the
 * festival when it is a good time to hold it, then Heritage to spend, then a village order
 * ready to send (D304: it goes at midnight, an upgrade waits), then an upgrade ready, then the
 * rest (the festival still growing, or the nearest goal, as `nextLine` has it).
 * Everything not shown here is still reachable from its own tab's badge.
 */
export function worldPrompt(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
  heritage: number | null,
  upgradeText: string | null,
  orderText: string | null = null,
  upgradeDetail: string | null = null,
): NextLine {
  if (idleFestivalAvailable(state, content)) {
    const advice = festivalAdvice(state, content, now);
    if (advice.advice === 'hold')
      return { kind: 'festival_ready', text: advice.short, progress: 1, tab: 'festival' };
  }
  if (heritage !== null)
    return {
      kind: 'heritage',
      text: `${cap(term('heritage'))} to spend: ${num(heritage)}`,
      progress: 1,
      tab: 'festival',
    };
  if (orderText !== null) return { kind: 'order', text: orderText, progress: 1, tab: 'region' };
  if (upgradeText !== null)
    return {
      kind: 'upgrade',
      text: upgradeText,
      ...(upgradeDetail !== null ? { detail: upgradeDetail } : {}),
      progress: 1,
      tab: 'almanac',
    };
  return nextLine(state, content);
}

// ---------------------------------------------------------------------------------------
// D288: seasons and the day, looks only, for the header's one small chip.

/** An icon for a season or a day phase; a UI glyph, not a content icon. */
export const SEASON_ICON: Record<string, string> = {
  spring: 'flower-2',
  summer: 'sun',
  autumn: 'leaf',
  winter: 'snowflake',
};
/** The season's plain name, from the content (D288). */
export function seasonName(content: IdleContent, season: IdleSeason): string {
  return content.seasons.list.find((s) => s.id === season.season)?.name ?? season.season;
}

// ---------------------------------------------------------------------------------------
// D289: a visitor at the gate, never a loss if missed (as a lucky find, D244, D254).

/** `The vet is at the gate`, from the visitor's name in the content. */
export function visitorText(content: IdleContent, visitor: string): string {
  const name = content.bonuses.visitors.kinds.find((v) => v.id === visitor)?.name ?? visitor;
  return `${name} is at the gate`;
}

// ---------------------------------------------------------------------------------------
// D290: the collection book, grouped by kind, the rest shown as outlines.

// D364: `marks` and `rosettes` take their word from the shown theme, not a fixed label.
const COLLECTION_KIND_LABEL: Omit<Record<IdleCollectionKind, string>, 'marks' | 'rosettes'> = {
  breeds: 'Breeds',
  tiers: 'Building tiers',
  monuments: 'Monument grounds',
  seasons: 'Seasons',
  visitors: 'Visitors',
  prizes: 'Weekly prizes',
  seasonal: 'Seasonal finds',
  sets: 'Decoration sets',
};

const VISITOR_ICON: Record<string, string> = {
  vet: 'stethoscope',
  delivery_van: 'truck',
  villager: 'user',
  inspector: 'clipboard-list',
  photographer: 'camera',
};

/** D304: a weekly event's own prize icon, by its id. */
const PRIZE_ICON: Record<string, string> = {
  spring_lamb: 'sprout',
  fair_rosette: 'ribbon',
  harvest_lantern: 'flame',
};

/** D330: a plain icon for every mark, instead of the one sun for all ten. */
const MARK_ICON: Record<string, string> = {
  monument: 'landmark',
  standing_stone: 'gem',
  hedgerow: 'fence',
  windmill: 'wind',
  dovecote: 'birdhouse',
  well: 'droplet',
  yew_tree: 'tree-pine',
  maypole: 'utility-pole',
  stone_bridge: 'bridge',
  beacon: 'flame',
};

/** D330: a building tier's own icon, instead of the one barn for all three. */
const TIER_ICON: Record<string, string> = {
  lean_to: 'tent',
  timber: 'home',
  stone: 'warehouse',
};

/** D327: the monument-ground styles, taken in turn once the marks run out. */
const MONUMENT_ICON: Record<string, string> = {
  circle: 'circle',
  chapel: 'church',
  garden: 'leaf',
};

/** D331: a seasonal decoration's icon, by what it restyles. */
const SEASONAL_ICON: Record<string, string> = {
  path: 'route',
  hedge: 'trees',
  gate: 'door-open',
  vehicle: 'truck',
  worker: 'user',
  building: 'warehouse',
  roof: 'home',
  fence: 'fence',
};

/** D331: a seasonal decoration's own icon, where its kind alone would repeat (two worker finds). */
const SEASONAL_ID_ICON: Record<string, string> = {
  path_autumn_leaves: 'leaf',
  hedge_may_blossom: 'flower-2',
  gate_primrose: 'sprout',
  worker_show_best: 'trophy',
  worker_harvest_gold: 'flame',
};

/** D326: a rosette's own icon, in the order the data lists them. */
const ROSETTE_ICON = ['ribbon', 'medal', 'award', 'trophy', 'star'];

export interface CollectionRow {
  kind: IdleCollectionKind;
  id: string;
  /** Null while not found: shown as an outline, its name not given away. */
  name: string | null;
  icon: string;
  found: boolean;
}

export interface CollectionGroup {
  kind: IdleCollectionKind;
  label: string;
  found: number;
  total: number;
  rows: CollectionRow[];
}

/** Every kind's icon, in content order, grouped for the Almanac's Collection page (D290). */
export function collectionGroups(
  content: IdleContent,
  view: IdleCollectionView,
): CollectionGroup[] {
  const icon = (row: IdleCollectionView['items'][number]): string => {
    if (row.kind === 'breeds') {
      const producer = content.collection.breeds.find((b) => b.id === row.id)?.producer;
      return producer ? iconFor(producer) : 'sprout';
    }
    if (row.kind === 'tiers') return TIER_ICON[row.id] ?? 'warehouse';
    if (row.kind === 'marks') return MARK_ICON[row.id] ?? 'landmark';
    if (row.kind === 'monuments') return MONUMENT_ICON[row.id] ?? 'circle';
    if (row.kind === 'seasons') return SEASON_ICON[row.id] ?? 'sun';
    // D304: a themed week's prize.
    if (row.kind === 'prizes') return PRIZE_ICON[row.id] ?? 'gift';
    if (row.kind === 'seasonal') {
      if (SEASONAL_ID_ICON[row.id]) return SEASONAL_ID_ICON[row.id]!;
      const kind = content.decorations.find((d) => d.id === row.id)?.kind;
      return (kind && SEASONAL_ICON[kind]) ?? 'sparkles';
    }
    if (row.kind === 'rosettes') {
      const i = content.collection.rosettes.findIndex((r) => r.id === row.id);
      return ROSETTE_ICON[i % ROSETTE_ICON.length] ?? 'ribbon';
    }
    // D331: a set's id is the decoration kind it names.
    if (row.kind === 'sets') return SEASONAL_ICON[row.id] ?? 'box';
    return VISITOR_ICON[row.id] ?? 'user';
  };
  const kinds: IdleCollectionKind[] = [
    'breeds',
    'tiers',
    'marks',
    'monuments',
    'seasons',
    'visitors',
    'prizes',
    'seasonal',
    'rosettes',
    'sets',
  ];
  return (
    kinds
      .map((kind) => {
        const items = view.items.filter((i) => i.kind === kind);
        return {
          kind,
          label:
            kind === 'marks'
              ? term('marks')
              : kind === 'rosettes'
                ? `${cap(term('rosette'))}s`
                : COLLECTION_KIND_LABEL[kind],
          found: items.filter((i) => i.found).length,
          total: items.length,
          rows: items.map((i) => ({
            kind,
            id: i.id,
            name: i.found ? (i.name ?? i.id) : null,
            icon: icon(i),
            found: i.found,
          })),
        };
      })
      // D331: a kind with nothing in the data yet (decoration sets, until another lane names
      // one) stays hidden rather than showing an empty "0 of 0" section.
      .filter((g) => g.total > 0)
  );
}

// ---------------------------------------------------------------------------------------
// D291, D331: decorations restyle the world, bought with Trimmings, kept for good.

const DECORATION_KIND_LABEL: Record<IdleDecorationKind, string> = {
  building: 'Buildings',
  roof: 'Roofs',
  path: 'Paths',
  fence: 'Fences',
  hedge: 'Hedges',
  gate: 'Gates',
  vehicle: 'Vehicles',
  worker: 'Workers',
};

export interface DecorationOption {
  id: string;
  kind: IdleDecorationKind;
  name: string;
  /** In Trimmings. */
  cost: number;
  owned: boolean;
  /** On the world now. */
  selected: boolean;
  /** Being tried on the map now. */
  trying: boolean;
  /** Trimmings short, when it can be bought but not afforded. */
  needs?: number;
  /** D331: why it cannot be bought yet: "Opens after 3 festivals". */
  locked?: string;
  /** D331: a seasonal decoration on sale this week only. */
  thisWeek?: boolean;
}

export interface DecorationGroup {
  kind: IdleDecorationKind;
  label: string;
  options: DecorationOption[];
}

/**
 * Every decoration, by kind, in content order, with what the world owns, has chosen and is
 * trying on (D291, D331). A seasonal one not on sale this week is listed only once owned.
 */
export function decorationGroups(
  content: IdleContent,
  state: IdleWorldState,
  now: number,
  trying: Partial<Record<IdleDecorationKind, string>> = {},
): DecorationGroup[] {
  const chosen = idleDecorations(state, content);
  const have = idleTrimmings(state);
  return IDLE_DECORATION_KINDS.map((kind) => ({
    kind,
    label: DECORATION_KIND_LABEL[kind],
    options: content.decorations
      .filter((d) => d.kind === kind)
      .filter(
        (d) =>
          idleDecorationOwned(state, content, d.id) ||
          idleDecorationInSeason(state, content, d, now),
      )
      .map((d) => {
        const owned = idleDecorationOwned(state, content, d.id);
        const option: DecorationOption = {
          id: d.id,
          kind,
          name: d.name,
          cost: d.cost,
          owned,
          selected: chosen[kind] === d.id,
          trying: trying[kind] === d.id,
        };
        if (owned) return option;
        if (d.event !== undefined) option.thisWeek = true;
        const wait = idleDecorationFestivalsNeeded(state, d);
        if (wait > 0) option.locked = opensAfter(wait, state.permanent.festivals.length);
        else if (have < d.cost) option.needs = d.cost - have;
        return option;
      }),
  }));
}
